/**
 * Holds the active `AppConfig`; `setApp` resets per-app stores synchronously so a new app's view never mounts against the previous app's data.
 * `clear()` does the same reset for apps with their own layout (gis-services, AI), since a nulled app stops `setApp` detecting the change.
 */

import { getLogger } from '@logtape/logtape';
import type { AppConfig } from '#lib/apps/types.js';
import { geodataStore } from '#lib/stores/geodataStore.svelte.js';
import { legendStore } from '#lib/stores/legendStore.svelte.js';
import { mapDataFilterStore } from '#lib/stores/mapDataFilterStore.svelte.js';
import { featureSelectionStore } from '#lib/stores/featureSelectionStore.svelte.js';
import { mireExplorerStore } from '#lib/stores/mireExplorerStore.svelte.js';
import { resetMapExtent } from '#lib/stores/mapExtentStore.svelte.js';
import { agentFilterStore } from '#lib/stores/agentFilterStore.svelte.js';

const log = getLogger(['platform', 'app-store']);

// Stored as `AppConfig<unknown>` so per-app generic instantiations are
// assignment-compatible (the `method` field is contravariant in `T`).
// Consumers that know the active app's `T` cast `geodataStore.systemData` at
// the call site. Raw so a repeat `setApp` (e.g. a hover preload re-running the `[app]` load) is a no-op, not a new proxy.
let app = $state.raw<AppConfig<unknown> | null>(null);

function resetPerAppState() {
	geodataStore.resetAppData();
	legendStore.clear();
	mapDataFilterStore.resetAll();
	featureSelectionStore.clearSelectedFeatures();
	mireExplorerStore.lineColorExpression = [];
	resetMapExtent();
	// Read by syncVisibleLayers for every app, so left on it hides the next app's layers.
	agentFilterStore.setAiMode(false);
}

export const appStore = {
	get app() {
		return app;
	},
	setApp<T>(config: AppConfig<T>) {
		const previousId = app?.id;
		app = config as AppConfig<unknown>;
		if (previousId !== config.id) {
			log.info('{name} {version}', { name: config.name, version: config.version });
			if (previousId !== undefined) resetPerAppState();
		}
	},
	// Also run by a data-version switch (`(product)/+layout.svelte`).
	resetPerAppState,
	clear() {
		app = null;
		resetPerAppState();
	}
};
